import { formatApiErrorMessage } from "@/src/lib/error-utils";

export const getErrorMessage = (err: any, fallback: string = "An error occurred"): string => {
  return formatApiErrorMessage(err, fallback);
};

export { formatApiErrorMessage };

export interface CoursePreview {
  id: number;
  uuid: string;
  title: string;
  description?: string | null;
  cover_image_url?: string | null;
  total_modules: number;
  total_lessons: number;
}

export interface DepartmentStat {
  department: string;
  total_members: number;
  active_learners: number;
  avg_quiz_score: number;
  weak_skills: string[];
  competency_progress: number;
}

export interface OrgStats {
  organization_id: number;
  organization_name: string;
  name?: string;
  slug: string;
  plan_tier?: string | null;
  status: string;
  industry?: string | null;
  company_size?: string | null;
  join_token?: string | null;
  join_link_enabled: boolean;
  allowed_domains: string[];
  departments: string[];
  department_stats: DepartmentStat[];
  logo_url?: string | null;
  description?: string | null;
  seat_limit?: number | null;
  total_members: number;
  active_learners: number;
  total_completions: number;
  avg_quiz_score: number;
  weak_skills: string[];
  competency_progress: number;
  sponsored_courses: CoursePreview[];
}

export interface MemberRow {
  user_id: number;
  clerk_user_id?: string | null;
  name?: string | null;
  email?: string | null;
  role: string;
  department?: string | null;
  job_title?: string | null;
  experience_level?: string | null;
  joined_at?: string | null;
  courses_enrolled: number;
  courses_completed: number;
  quizzes_passed: number;
  last_active_at?: string | null;
  gaps_count?: number;
  at_risk?: boolean;
  overdue_count?: number;
  avg_quiz_score?: number;
  status_pill?: "top_performer" | "on_track" | "gaps_detected" | "overdue" | "approaching_deadline" | string;
}

export interface OrgDocument {
  id: number;
  uuid: string;
  organization_id: number;
  workspace_id?: number | null;
  title: string;
  document_type: string;
  file_url: string;
  file_size_bytes?: number | null;
  description?: string | null;
  status?: string;
  chunks_count?: number;
  created_at: string;
}

export interface InvitationItem {
  id: number;
  uuid: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
  invite_url?: string;
  department?: string | null;
  job_title?: string | null;
  workspace_id?: number | null;
  workspace_name?: string | null;
  workspace_slug?: string | null;
}

export interface WorkspaceItem {
  id: number;
  uuid: string;
  name: string;
  slug: string;
  description?: string | null;
  is_default: boolean;
  seat_limit?: number | null;
  settings?: Record<string, any>;
  member_count: number;
  total_members?: number;
  created_at: string;
}

export interface WorkspaceMemberInfo {
  id: number;
  user_id: number;
  clerk_user_id?: string | null;
  email?: string | null;
  name?: string | null;
  role: string;
  job_title?: string | null;
  department?: string | null;
  joined_at: string;
}

export interface WorkspaceDocumentItem {
  id: number;
  uuid: string;
  organization_id: number;
  workspace_id?: number | null;
  title: string;
  document_type: string;
  file_url: string;
  file_size_bytes?: number | null;
  description?: string | null;
  status?: string;
  chunks_count?: number;
  created_at: string;
}

export interface WorkspaceCourseItem {
  id: number;
  uuid: string;
  title: string;
  description?: string | null;
  cover_image_url?: string | null;
  total_modules: number;
  total_lessons: number;
  assigned_at?: string | null;
  linked_at?: string | null;
}

export type WorkspaceCustomCourseRequestItem = WorkspaceCustomRequestItem;
export type WorkspaceAnalytics = WorkspaceAnalyticsData;

export interface WorkspaceCustomRequestItem {
  id: number;
  uuid: string;
  workspace_id: number;
  workspace_name?: string | null;
  workspace_slug?: string | null;
  organization_id: number;
  organization_name?: string | null;
  requested_by: number;
  requester_name?: string | null;
  requester_email?: string | null;
  course_title: string;
  target_audience: string;
  learning_objectives: string;
  suggested_modules: string[];
  reference_document_ids: number[];
  status: string;
  image_url?: string | null;
  admin_feedback?: string | null;
  generated_course_id?: number | null;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceDetail {
  id: number;
  uuid: string;
  name: string;
  slug: string;
  description?: string | null;
  is_default: boolean;
  seat_limit?: number | null;
  settings?: Record<string, any>;
  member_count: number;
  created_at: string;
  members: WorkspaceMemberInfo[];
}

export interface WorkspaceLearningRuleItem {
  id: number;
  uuid: string;
  workspace_id: number;
  workspace_name?: string | null;
  workspace_slug?: string | null;
  course_id: number;
  course_title?: string | null;
  module_id?: number | null;
  module_title?: string | null;
  is_mandatory: boolean;
  passing_threshold_score: number;
  due_days_after_enrollment?: number | null;
  remediation_required: boolean;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceCourseAnalyticsItem {
  course_id: number;
  course_title: string;
  total_assigned_learners: number;
  completed_learners: number;
  avg_score: number;
  compliance_rate: number;
  is_mandatory: boolean;
}

export interface WorkspaceAnalyticsData {
  workspace_id: number;
  workspace_name: string;
  workspace_slug: string;
  total_members: number;
  active_learners: number;
  avg_quiz_score: number;
  compliance_rate: number;
  total_rules_configured: number;
  skills_tracked: number;
  skills_with_gaps: number;
  courses_breakdown: WorkspaceCourseAnalyticsItem[];
}

export interface WorkspaceSkillExpectationItem {
  id: number;
  organization_id: number;
  workspace_id?: number | null;
  workspace_name?: string | null;
  workspace_slug?: string | null;
  skill_name: string;
  target_proficiency: string;
  department?: string | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Employee Intelligence & Learning Dossier Types
// ---------------------------------------------------------------------------

export interface MemberCourseProgressItem {
  course_id: number;
  course_title: string;
  course_slug?: string | null;
  progress_percent: number;
  status: "on_track" | "stalled" | "overdue" | "completed" | string;
  total_modules: number;
  completed_modules: number;
  current_module_title?: string | null;
  due_date?: string | null;
  is_mandatory: boolean;
  last_activity_at?: string | null;
}

export interface MemberCompetencyDetailItem {
  id: number;
  skill_name: string;
  current_proficiency: string;
  target_proficiency: string;
  diagnosed_score: number;
  status: "gap_detected" | "in_progress" | "mastered" | string;
  weak_concepts: string[];
}

export interface MemberQuizAttemptItem {
  id: number;
  module_title: string;
  score: number;
  passed: boolean;
  attempt_number: number;
  submitted_at?: string | null;
}

export interface MemberRemediationSummary {
  id: number;
  skill_name: string;
  weak_concept: string;
  is_resolved: boolean;
  resolved_at?: string | null;
}

export interface AIManagerBriefingItem {
  executive_summary: string;
  key_strengths: string[];
  active_blockers: string[];
  suggested_1on1_talking_points: string[];
  suggested_actions: string[];
}

export interface MemberPeerPercentiles {
  quiz_performance_percentile: number;
  completion_velocity_ratio: number;
  department_rank: string;
  status_label: string;
}

export interface MemberDetailResponse {
  user_id: number;
  clerk_user_id?: string | null;
  name: string;
  email?: string | null;
  role: string;
  department?: string | null;
  job_title?: string | null;
  experience_level?: string | null;
  joined_at?: string | null;
  last_active_at?: string | null;
  streak_days: number;
  learning_hours_estimated: number;
  total_courses_enrolled: number;
  total_courses_completed: number;
  overall_avg_quiz_score: number;
  overall_status: "on_track" | "struggling" | "overdue" | "top_performer" | string;
  courses: MemberCourseProgressItem[];
  competencies: MemberCompetencyDetailItem[];
  weak_concepts: string[];
  active_remediations: MemberRemediationSummary[];
  recent_quizzes: MemberQuizAttemptItem[];
  peer_percentiles: MemberPeerPercentiles;
  ai_manager_briefing: AIManagerBriefingItem;
}
